import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { ProductLaunchpadItems, type ProductLaunchpadItemKey } from './components';

type ProductLaunchpadProps = ComponentProps<'section'> & {
  namespace: string;
  items: ProductLaunchpadItemKey[];
};

/**
 * ProductLaunchpad component is a component that displays a product launchpad section on home page.
 * It displays a title, description and a list of products. Each product is displayed as a product launchpad item.
 * Component extends the section HTML element.
 *
 * @param {ProductLaunchpadProps} props - The props for the ProductLaunchpad component.
 * @param {string} props.namespace - The namespace for the translations.
 * @param {string[]} props.items - The items to display in the product launchpad.
 * @returns {JSX.Element} The ProductLaunchpad component.
 */
function ProductLaunchpad({ className, namespace, items, ...props }: ProductLaunchpadProps) {
  const t = useTranslations(namespace);
  const classes = {
    section: twMerge('py-10 md:px-10 flex flex-col items-start text-left md:text-center md:items-center', className),
    description: 'paragraph mt-5',
    productLaunchpadItems: 'mt-10',
  };
  return (
    <section className={classes.section} data-track-container="Discover Products" {...props}>
      <h2 className="heading-3">{t('title')}</h2>
      <p className={classes.description}>{t('description')}</p>
      <ProductLaunchpadItems className={classes.productLaunchpadItems} items={items} />
    </section>
  );
}

export { ProductLaunchpad };
